import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import type { AnalyticsProperties } from "@/lib/analytics/events";

/**
 * Fires `loading_screen_shown` when a full-page/section loading state
 * becomes active and `loading_screen_resolved` (with how long it was on
 * screen) when it goes away — covers screens that mount only while loading
 * (pass `active: true`) and ones that stay mounted and toggle a boolean
 * (e.g. LoadingScreen's `isLoading` prop).
 *
 * Lets us see, per loading surface, how often it's actually hit and how
 * long people sit on it — the thing the new drawing/mark animation
 * (NovariMarkLoader) is meant to make feel shorter.
 */
export function useLoadingScreenAnalytics(
  active: boolean,
  screen: string,
  properties?: AnalyticsProperties
) {
  const shownAtRef = useRef<number | null>(null);
  const propsRef = useRef(properties);
  propsRef.current = properties;

  useEffect(() => {
    if (active) {
      shownAtRef.current = performance.now();
      track(ANALYTICS_EVENTS.LOADING_SCREEN_SHOWN, { screen, ...propsRef.current });
    } else if (shownAtRef.current !== null) {
      track(ANALYTICS_EVENTS.LOADING_SCREEN_RESOLVED, {
        screen,
        duration_ms: Math.round(performance.now() - shownAtRef.current),
        ...propsRef.current,
      });
      shownAtRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, screen]);

  // Also cover navigating away while still active (component unmounts
  // without `active` ever flipping to false).
  useEffect(() => {
    return () => {
      if (shownAtRef.current !== null) {
        track(ANALYTICS_EVENTS.LOADING_SCREEN_RESOLVED, {
          screen,
          duration_ms: Math.round(performance.now() - shownAtRef.current),
          ...propsRef.current,
        });
        shownAtRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
